import { useState, useCallback } from 'react';
import { IconTable, IconX } from '@tabler/icons-react';
import { useDialogFocus } from '../../hooks/useDialogFocus';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getDisplayLevel } from '@pokerroom/types';
import { api } from '../../lib/api';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import type { TableOverview } from './TableManagementPage';
import TimedTableCountPanel from './TimedTableCountPanel';

interface MemberSearchResult {
  member: { id: string; nickname: string; memberNo: string; phone?: string };
  hasDebt: boolean;
  isBlacklisted: boolean;
}

interface PlayerEntry {
  id: string;
  playerName: string;
  status: string;
  currentChips: number;
  memberId: string | null;
  seatNo: number | null;
  tableNo: number | null;
  rebuyCount: number;
  addonCount: number;
  totalBuyInChips: number;
}

interface RebuyAddonLog {
  id: string;
  type: 'REBUY' | 'ADDON' | 'REENTRY';
  chipsAdded: number;
  atLevel: number;
  createdAt: string;
}

interface TournamentDetail {
  id: string;
  type: string;
  manualCountMode: boolean;
  status: string;
  rebuyAllowed: boolean;
  rebuyDeadline: number;
  rebuyCost: string;
  rebuyChips: number;
  addonAllowed: boolean;
  addonDeadline: number;
  addonCost: string;
  addonChips: number;
  timerState: { level: number } | null;
  blindStructure?: {
    levels: Array<{ levelNo: number; isBreak: boolean }>;
  };
}

interface Props {
  data: TableOverview;
  onClose: () => void;
  tournaments: Array<{ id: string; name: string; status: string }>;
  allTables: TableOverview[];
}

type TabType = 'seats' | 'actions' | 'transfer';

/** Seat status → color classes for oval seat map */
function seatColor(status?: string) {
  switch (status) {
    case 'PLAYING':
    case 'FINAL_TABLE':
      return { border: 'border-emerald-500/60', bg: 'bg-emerald-500/20', text: 'text-emerald-400' };
    case 'CHECKED_IN':
      return { border: 'border-amber-500/60', bg: 'bg-amber-500/20', text: 'text-amber-400' };
    case 'ELIMINATED':
      return {
        border: 'border-red-500/60',
        bg: 'bg-red-500/20',
        text: 'text-red-400 line-through',
      };
    default:
      return { border: 'border-base-content/20', bg: '', text: 'text-base-content/40' };
  }
}

export default function TableDetailPanel({ data, onClose, tournaments, allTables }: Props) {
  const { t } = useTranslation('venue');
  const { isMobile } = useBreakpoint();
  const dialogRef = useDialogFocus(true);
  const qc = useQueryClient();
  const tid = data.assignment?.tournamentId;

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('seats');

  // Seat tab states
  const [assigningSeatNo, setAssigningSeatNo] = useState<number | null>(null);
  const [selectedSeatNo, setSelectedSeatNo] = useState<number | null>(null);

  // Tournament management states
  const [assignTournamentId, setAssignTournamentId] = useState('');

  // Player actions tab states
  const [memberSearch, setMemberSearch] = useState('');
  const [anonymousName, setAnonymousName] = useState('');
  const [confirmEliminate, setConfirmEliminate] = useState<string | null>(null);

  // Correction modal state
  const [correctingEntry, setCorrectingEntry] = useState<PlayerEntry | null>(null);
  const [correctPassword, setCorrectPassword] = useState('');
  const [correctError, setCorrectError] = useState('');

  // Settling (chip counting) state — use string to allow free typing
  const [chipInputs, setChipInputs] = useState<Record<string, string>>({});

  // Transfer tab states
  const [transferEntry, setTransferEntry] = useState<{
    entryId: string;
    fromSeatNo: number;
  } | null>(null);
  const [targetTableId, setTargetTableId] = useState<string>('');
  const [targetSeatNo, setTargetSeatNo] = useState<number | null>(null);

  const maxSeats = data.table.maxSeats;
  const seatMap = new Map(data.seats.map((s) => [s.seatNo, s]));

  // --- Queries ---

  // Fetch entries for the assigned tournament
  const { data: entries = [] } = useQuery<PlayerEntry[]>({
    queryKey: ['tournament-entries', tid],
    queryFn: () => api.get<PlayerEntry[]>(`/tournament/${tid}/entries`),
    enabled: !!tid,
    refetchInterval: 5_000,
  });

  // Fetch tournament detail for rebuy/addon settings
  const { data: tournamentDetail } = useQuery<TournamentDetail>({
    queryKey: ['tournament-detail', tid],
    queryFn: () => api.get<TournamentDetail>(`/tournament/${tid}`),
    enabled: !!tid,
    staleTime: 30_000,
  });

  const isSettling = !!(tournamentDetail?.timerState as Record<string, unknown> | null)?.settling;
  const isTimedSettlement = tournamentDetail?.type === 'TIMED' && tournamentDetail.manualCountMode;

  const rawLevel = tournamentDetail?.timerState?.level ?? 0;
  const bsLevels = tournamentDetail?.blindStructure?.levels ?? [];
  const currentDisplayLevel = rawLevel > 0 ? getDisplayLevel(bsLevels, rawLevel) : 0;
  const canRebuy =
    !!tournamentDetail?.rebuyAllowed && currentDisplayLevel <= tournamentDetail.rebuyDeadline;
  const canAddon =
    !!tournamentDetail?.addonAllowed && currentDisplayLevel <= tournamentDetail.addonDeadline;

  // Member search
  const { data: searchResults = [] } = useQuery<MemberSearchResult[]>({
    queryKey: ['check-in-search', memberSearch],
    queryFn: () =>
      api.get<MemberSearchResult[]>(
        `/members/check-in/search?q=${encodeURIComponent(memberSearch)}`,
      ),
    enabled: memberSearch.length >= 1,
    staleTime: 2_000,
  });

  // --- Mutations ---

  const invalidateAll = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['tables-overview'] });
    if (tid) qc.invalidateQueries({ queryKey: ['tournament-entries', tid] });
  }, [qc, tid]);

  const assignMutation = useMutation({
    mutationFn: (tournamentId: string) =>
      api.post(`/venue/tournaments/${tournamentId}/tables`, { tableId: data.table.id }),
    onSuccess: () => {
      invalidateAll();
      setAssignTournamentId('');
    },
    onError: (err: Error) => {
      console.warn('assignTable failed:', err.message);
    },
  });

  const unassignMutation = useMutation({
    mutationFn: () => api.delete(`/venue/tournaments/${tid!}/tables/${data.table.id}`),
    onSuccess: invalidateAll,
    onError: (err: Error) => {
      console.warn('unassignTable failed:', err.message);
    },
  });

  const breakTableMutation = useMutation({
    mutationFn: () => api.post(`/venue/tables/${data.table.id}/break`),
    onSuccess: invalidateAll,
    onError: (err: Error) => {
      console.warn('breakTable failed:', err.message);
    },
  });

  const clearSeatsMutation = useMutation({
    mutationFn: () => api.post(`/venue/tables/${data.table.id}/clear-seats`),
    onSuccess: invalidateAll,
    onError: (err: Error) => {
      console.warn('clearSeats failed:', err.message);
    },
  });

  const seatAssignMutation = useMutation({
    mutationFn: ({ entryId, seatNo }: { entryId: string; seatNo: number }) =>
      api.post(`/venue/tournaments/${tid!}/seats`, { entryId, tableId: data.table.id, seatNo }),
    onSuccess: () => {
      invalidateAll();
      setAssigningSeatNo(null);
    },
    onError: (err: Error) => {
      console.warn('seatAssign failed:', err.message);
    },
  });

  const registerMutation = useMutation({
    mutationFn: (body: { memberId?: string; playerName?: string }) =>
      api.post(`/tournament/${tid!}/entries`, body),
    onSuccess: () => {
      invalidateAll();
      setMemberSearch('');
      setAnonymousName('');
    },
    onError: (err: Error) => {
      console.warn('register failed:', err.message);
    },
  });

  const checkInMutation = useMutation({
    mutationFn: (entryId: string) => api.post(`/tournament/${tid!}/entries/${entryId}/check-in`),
    onSuccess: invalidateAll,
    onError: (err: Error) => {
      console.warn('checkIn failed:', err.message);
    },
  });

  const eliminateMutation = useMutation({
    mutationFn: ({ entryId, rank }: { entryId: string; rank: number }) =>
      api.post(`/tournament/${tid!}/eliminate`, { entryId, rank }),
    onSuccess: () => {
      invalidateAll();
      setConfirmEliminate(null);
      setSelectedSeatNo(null);
    },
    onError: (err: Error) => {
      console.warn('eliminate failed:', err.message);
    },
  });

  const rebuyAddonMutation = useMutation({
    mutationFn: ({ entryId, type }: { entryId: string; type: 'REBUY' | 'ADDON' }) =>
      api.post(`/tournament/${tid!}/rebuy-addon`, { entryId, type }),
    onSuccess: () => {
      invalidateAll();
      setSelectedSeatNo(null);
    },
    onError: (err: Error) => {
      console.warn('rebuyAddon failed:', err.message);
    },
  });

  const transferMutation = useMutation({
    mutationFn: (body: {
      entryId: string;
      fromTableId: string;
      fromSeatNo: number;
      toTableId: string;
      toSeatNo: number;
    }) => api.post(`/venue/tournaments/${tid!}/transfers`, body),
    onSuccess: () => {
      invalidateAll();
      setTransferEntry(null);
      setTargetTableId('');
      setTargetSeatNo(null);
    },
    onError: (err: Error) => {
      console.warn('transfer failed:', err.message);
    },
  });

  const correctMutation = useMutation({
    mutationFn: ({ logId, password }: { logId: string; password: string }) =>
      api.post(`/tournament/${tid!}/correct-entry`, { logId, password }),
    onSuccess: () => {
      invalidateAll();
      setCorrectPassword('');
      setCorrectError('');
    },
    onError: (err: Error) => {
      setCorrectError(err.message || '修正失敗');
    },
  });

  const batchChipsMutation = useMutation({
    mutationFn: (updates: Array<{ entryId: string; currentChips: number }>) =>
      api.patch(`/tournament/${tid!}/entries/batch-chips`, { updates }),
    onSuccess: () => {
      invalidateAll();
      setChipInputs({});
    },
    onError: (err: Error) => {
      setCorrectError(err.message || '籌碼更新失敗');
    },
  });

  // Fetch rebuy/addon logs for the correcting entry
  const { data: correctingLogs = [] } = useQuery<RebuyAddonLog[]>({
    queryKey: ['rebuy-addon-logs', tid, correctingEntry?.id],
    queryFn: () =>
      api.get<RebuyAddonLog[]>(`/tournament/${tid}/rebuy-addon-logs/${correctingEntry!.id}`),
    enabled: !!tid && !!correctingEntry,
  });

  // --- Derived data ---

  const activeTournaments = tournaments.filter(
    (t) => t.status === 'IN_PROGRESS' || t.status === 'REGISTERED' || t.status === 'PAUSED',
  );

  // Unassigned players eligible for seating (CHECKED_IN or PLAYING without a seat)
  const unseatedPlayers = entries.filter(
    (e) => (e.status === 'CHECKED_IN' || e.status === 'PLAYING') && !e.seatNo,
  );

  // Players by status
  const registeredEntries = entries.filter((e) => e.status === 'REGISTERED');
  const activeEntries = entries.filter((e) => e.status === 'PLAYING' || e.status === 'FINAL_TABLE');

  // Count of alive players (for auto-rank on elimination)
  const aliveCount = entries.filter(
    (e) => e.status === 'PLAYING' || e.status === 'FINAL_TABLE',
  ).length;

  // Seated players on this table (for transfer tab)
  const seatedPlayers = data.seats
    .filter((s) => s.entry && (s.entry.status === 'PLAYING' || s.entry.status === 'FINAL_TABLE'))
    .map((s) => ({ entryId: s.entryId, seatNo: s.seatNo, playerName: s.entry!.playerName }));

  // Target table for transfer
  const targetTable = allTables.find((t) => t.table.id === targetTableId);
  const otherTables = allTables.filter(
    (t) => t.table.id !== data.table.id && t.assignment?.tournamentId === tid,
  );

  // Selected seat's entry (for quick action menu)
  const selectedSeat = selectedSeatNo ? seatMap.get(selectedSeatNo) : null;
  const selectedEntry = selectedSeat?.entry;

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="table-detail-title"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      className={`${
        isMobile
          ? 'fixed inset-0 z-50 bg-base-100 overflow-y-auto'
          : 'fixed right-0 top-0 bottom-0 w-[480px] z-50 bg-base-200 border-l border-base-300 overflow-y-auto'
      }`}
    >
      {/* Header */}
      <div className="sticky top-0 bg-base-200 border-b border-base-300 px-4 py-2 flex items-center justify-between z-10">
        <h2 id="table-detail-title" className="text-lg font-bold flex items-center gap-2">
          <IconTable size={20} stroke={1.5} aria-hidden="true" className="text-primary" />
          {t('tableLabel', { no: data.table.tableNo })}{' '}
          {data.table.name ? `— ${data.table.name}` : ''}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common:close')}
          className="w-11 h-11 flex items-center justify-center hover:bg-base-300"
        >
          <IconX size={20} stroke={1.5} aria-hidden="true" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Tournament Section */}
        <section>
          {data.tournament ? (
            <div className="bg-base-300 rounded-lg p-3 flex items-center justify-between">
              <div>
                <span className="font-medium text-sm">{data.tournament.name}</span>
                <span
                  className={`text-xs ml-2 px-2 py-0.5 rounded-full font-medium ${
                    isSettling ? 'bg-amber-500/20 text-amber-400' : 'bg-primary/20 text-primary'
                  }`}
                >
                  {isSettling ? '結算中' : data.tournament.status}
                </span>
              </div>
              <button
                onClick={() => unassignMutation.mutate()}
                disabled={unassignMutation.isPending}
                className="text-xs text-error hover:underline"
              >
                {t('unassignTournament')}
              </button>
            </div>
          ) : (
            <div className="bg-base-300 rounded-lg p-4 space-y-3">
              <p className="text-sm text-base-content/50">{t('tableIdle')}</p>
              <select
                value={assignTournamentId}
                onChange={(e) => setAssignTournamentId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-base-100 border border-base-300 text-sm"
              >
                <option value="">{t('selectTournament')}</option>
                {activeTournaments.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.status})
                  </option>
                ))}
              </select>
              {assignTournamentId && (
                <button
                  onClick={() => assignMutation.mutate(assignTournamentId)}
                  disabled={assignMutation.isPending}
                  className="w-full py-2 rounded-lg bg-primary text-primary-content text-sm font-medium"
                >
                  {t('assignTournament')}
                </button>
              )}
            </div>
          )}
        </section>

        {tid && isTimedSettlement && (
          <TimedTableCountPanel
            tournamentId={tid}
            tableId={data.table.id}
            tableNo={data.table.tableNo}
            maxSeats={data.table.maxSeats}
            canCount={isSettling || tournamentDetail.status === 'COMPLETED'}
            assignedTables={allTables
              .filter((table) => table.assignment?.tournamentId === tid)
              .map((table) => ({
                id: table.table.id,
                tableNo: table.table.tableNo,
                maxSeats: table.table.maxSeats,
              }))}
          />
        )}

        {/* Tabs — only show when tournament is assigned */}
        {tid && tournamentDetail && !isTimedSettlement && (
          <>
            <div className="flex border-b border-base-300">
              {(['seats', 'actions', 'transfer'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    setAssigningSeatNo(null);
                    setSelectedSeatNo(null);
                    setConfirmEliminate(null);
                  }}
                  className={`flex-1 min-h-[48px] text-sm font-medium transition border-b-2 ${
                    activeTab === tab
                      ? 'border-primary text-primary'
                      : 'border-transparent text-base-content/50 hover:text-base-content'
                  }`}
                >
                  {tab === 'seats'
                    ? t('seatsTab')
                    : tab === 'actions'
                      ? t('playerActionsTab')
                      : t('transferTab')}
                </button>
              ))}
            </div>

            {/* ====== SEATS TAB ====== */}
            {activeTab === 'seats' && (
              <div className="space-y-4">
                {/* Oval Seat Map */}
                <div className="relative bg-base-300 rounded-2xl p-6 min-h-[280px]">
                  <div className="absolute inset-8 rounded-[50%] border-2 border-primary/20 bg-primary/5" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs text-base-content/30 font-medium">
                      TABLE {data.table.tableNo}
                    </span>
                  </div>

                  <div className="relative w-full" style={{ paddingTop: '70%' }}>
                    {/* Dealer marker at 12 o'clock */}
                    <div
                      className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
                      style={{ left: '50%', top: `${50 - 38}%` }}
                    >
                      <div className="border border-amber-500/60 bg-amber-500/20 rounded-lg px-3 py-1 text-center">
                        <div className="text-[10px] text-amber-400 font-bold">DEALER</div>
                      </div>
                    </div>

                    {Array.from({ length: maxSeats }, (_, i) => i + 1).map((seatNo) => {
                      const seat = seatMap.get(seatNo);
                      const status = seat?.entry?.status;
                      const colors = seatColor(status);
                      // Seats distributed from ~1 o'clock to ~11 o'clock, leaving gap at 12 for Dealer
                      const gapDeg = 50; // degrees reserved for dealer at top
                      const arcDeg = 360 - gapDeg;
                      const startAngle = -90 + gapDeg / 2; // start just clockwise from 12 o'clock
                      const stepDeg = arcDeg / (maxSeats - 1 || 1);
                      const angleDeg = startAngle + stepDeg * (seatNo - 1);
                      const angle = (angleDeg * Math.PI) / 180;
                      const rx = 44;
                      const ry = 38;
                      const cx = 50 + rx * Math.cos(angle);
                      const cy = 50 + ry * Math.sin(angle);
                      const isAssigning = assigningSeatNo === seatNo;
                      const isSelected = selectedSeatNo === seatNo;

                      return (
                        <div
                          key={seatNo}
                          className="absolute transform -translate-x-1/2 -translate-y-1/2"
                          style={{ left: `${cx}%`, top: `${cy}%` }}
                        >
                          {seat ? (
                            <button
                              onClick={() => {
                                setAssigningSeatNo(null);
                                setSelectedSeatNo(isSelected ? null : seatNo);
                              }}
                              className={`${colors.bg} border ${colors.border} rounded-lg px-2 py-1.5 text-center min-w-[80px] min-h-[56px] cursor-pointer hover:brightness-125 transition ${
                                isSelected ? 'ring-2 ring-primary' : ''
                              }`}
                            >
                              <div className={`text-[10px] ${colors.text}`}>#{seatNo}</div>
                              <div
                                className={`text-xs font-medium truncate max-w-[80px] ${colors.text}`}
                              >
                                {seat.entry?.playerName ?? '?'}
                              </div>
                              {seat.entry?.currentChips != null && seat.entry.currentChips > 0 && (
                                <div className="text-[10px] text-primary">
                                  {seat.entry.currentChips.toLocaleString()}
                                </div>
                              )}
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedSeatNo(null);
                                setAssigningSeatNo(isAssigning ? null : seatNo);
                              }}
                              className={`border border-dashed rounded-lg px-2 py-1.5 text-center min-w-[56px] min-h-[48px] transition ${
                                isAssigning
                                  ? 'border-primary bg-primary/10'
                                  : 'border-base-content/20 opacity-50 hover:opacity-80'
                              }`}
                            >
                              <div className="text-[10px] text-base-content/40">#{seatNo}</div>
                              <div className="text-xs text-base-content/30">{t('empty')}</div>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Assign player to empty seat */}
                {assigningSeatNo && (
                  <div className="bg-base-300 rounded-lg p-3 space-y-2">
                    <div className="text-sm font-medium">
                      {t('assignPlayerToSeat', { seatNo: assigningSeatNo })}
                    </div>
                    {unseatedPlayers.length === 0 ? (
                      <p className="text-sm text-base-content/50">{t('noUnsettedCheckedIn')}</p>
                    ) : (
                      <div className="space-y-1 max-h-[200px] overflow-y-auto">
                        {unseatedPlayers.map((entry) => (
                          <button
                            key={entry.id}
                            onClick={() =>
                              seatAssignMutation.mutate({
                                entryId: entry.id,
                                seatNo: assigningSeatNo,
                              })
                            }
                            disabled={seatAssignMutation.isPending}
                            className="w-full text-left px-3 py-2 rounded-lg bg-base-100 hover:bg-primary/10 text-sm flex items-center justify-between min-h-[44px]"
                          >
                            <span>{entry.playerName}</span>
                            <span
                              className={`text-xs ${entry.status === 'PLAYING' ? 'text-emerald-400' : 'text-amber-400'}`}
                            >
                              {entry.status === 'PLAYING' ? t('playing') : t('checkedIn')}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => setAssigningSeatNo(null)}
                      className="text-xs text-base-content/50 hover:underline"
                    >
                      {t('cancel')}
                    </button>
                  </div>
                )}

                {/* Quick action menu for selected occupied seat */}
                {selectedSeatNo &&
                  selectedEntry &&
                  (() => {
                    const enriched = entries.find((e) => e.id === selectedSeat!.entryId);
                    return (
                      <div className="bg-base-300 rounded-lg p-3 space-y-3">
                        {/* Row 1: Player identity */}
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium">
                            T{data.table.tableNo} S{selectedSeatNo} — {selectedEntry.playerName}
                            <span
                              className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                                selectedEntry.status === 'PLAYING'
                                  ? 'bg-emerald-500/20 text-emerald-400'
                                  : selectedEntry.status === 'ELIMINATED'
                                    ? 'bg-red-500/20 text-red-400'
                                    : 'bg-base-100 text-base-content/50'
                              }`}
                            >
                              {selectedEntry.status}
                            </span>
                          </div>
                          <button
                            onClick={() => setSelectedSeatNo(null)}
                            className="text-xs text-base-content/50 hover:underline"
                          >
                            {t('closeMenu')}
                          </button>
                        </div>

                        {/* Row 2: Chips + stats (synced with player actions tab) */}
                        {enriched && (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 text-[10px] text-base-content/40">
                              <span>
                                {t('totalBuyIn', { defaultValue: '總買入' })}:{' '}
                                {enriched.totalBuyInChips?.toLocaleString()}
                              </span>
                              {enriched.rebuyCount > 0 && (
                                <span className="text-blue-400">R ×{enriched.rebuyCount}</span>
                              )}
                              {enriched.addonCount > 0 && (
                                <span className="text-purple-400">A ×{enriched.addonCount}</span>
                              )}
                            </div>
                            {enriched.currentChips > 0 && (
                              <span className="text-xs text-primary font-mono">
                                {enriched.currentChips.toLocaleString()}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Settling mode: chip input + confirm */}
                        {isSettling && enriched && (
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-base-content/50 whitespace-nowrap">
                              {t('chipCount', { defaultValue: '結算籌碼' })}:
                            </label>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={
                                chipInputs[selectedSeat!.entryId] ?? String(enriched.currentChips)
                              }
                              onChange={(e) =>
                                setChipInputs((prev) => ({
                                  ...prev,
                                  [selectedSeat!.entryId]: e.target.value.replace(/[^0-9]/g, ''),
                                }))
                              }
                              className="flex-1 px-3 py-2 rounded-lg bg-base-100 border border-amber-500/30 text-sm font-mono min-h-[44px] text-amber-300"
                            />
                            <button
                              onClick={() => {
                                const chips =
                                  parseInt(
                                    chipInputs[selectedSeat!.entryId] ??
                                      String(enriched.currentChips),
                                    10,
                                  ) || 0;
                                batchChipsMutation.mutate([
                                  { entryId: selectedSeat!.entryId, currentChips: chips },
                                ]);
                              }}
                              disabled={batchChipsMutation.isPending}
                              className="px-3 py-2 rounded-lg bg-amber-600/20 border border-amber-500/50 text-amber-300 text-sm font-medium min-h-[44px] hover:bg-amber-600/30 whitespace-nowrap"
                            >
                              {t('confirmChips', { defaultValue: '確認' })}
                            </button>
                          </div>
                        )}

                        <div className="flex gap-2 flex-wrap">
                          {(selectedEntry.status === 'PLAYING' ||
                            selectedEntry.status === 'FINAL_TABLE') &&
                            !isSettling && (
                              <>
                                {/* Eliminate */}
                                {confirmEliminate === selectedSeat!.entryId ? (
                                  <button
                                    onClick={() =>
                                      eliminateMutation.mutate({
                                        entryId: selectedSeat!.entryId,
                                        rank: aliveCount,
                                      })
                                    }
                                    disabled={eliminateMutation.isPending}
                                    className="px-3 rounded bg-red-600 text-white text-xs font-medium min-h-[44px]"
                                  >
                                    {t('confirmEliminateRank', { rank: aliveCount })}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => setConfirmEliminate(selectedSeat!.entryId)}
                                    className="px-3 rounded border border-red-500/50 text-red-400 text-xs font-medium min-h-[44px] hover:bg-red-500/10"
                                  >
                                    {t('eliminate', { ns: 'tournament' })}
                                  </button>
                                )}
                                {/* Rebuy */}
                                {canRebuy && (
                                  <button
                                    onClick={() =>
                                      rebuyAddonMutation.mutate({
                                        entryId: selectedSeat!.entryId,
                                        type: 'REBUY',
                                      })
                                    }
                                    disabled={rebuyAddonMutation.isPending}
                                    className="px-3 rounded border border-blue-500/50 text-blue-400 text-xs font-medium min-h-[44px] hover:bg-blue-500/10"
                                  >
                                    Rebuy +{tournamentDetail?.rebuyChips.toLocaleString()}
                                  </button>
                                )}
                                {/* Addon */}
                                {canAddon && (
                                  <button
                                    onClick={() =>
                                      rebuyAddonMutation.mutate({
                                        entryId: selectedSeat!.entryId,
                                        type: 'ADDON',
                                      })
                                    }
                                    disabled={rebuyAddonMutation.isPending}
                                    className="px-3 rounded border border-purple-500/50 text-purple-400 text-xs font-medium min-h-[44px] hover:bg-purple-500/10"
                                  >
                                    Addon +{tournamentDetail?.addonChips.toLocaleString()}
                                  </button>
                                )}
                                {/* Correction */}
                                {enriched &&
                                  (enriched.rebuyCount > 0 || enriched.addonCount > 0) && (
                                    <button
                                      onClick={() => {
                                        setCorrectingEntry(enriched);
                                        setCorrectPassword('');
                                        setCorrectError('');
                                      }}
                                      className="px-3 rounded border border-amber-500/50 text-amber-400 text-xs font-medium min-h-[44px] hover:bg-amber-500/10"
                                    >
                                      {t('correct', { defaultValue: '修正' })}
                                    </button>
                                  )}
                              </>
                            )}
                          {/* Transfer shortcut (hide in settling mode) */}
                          {!isSettling && (
                            <button
                              onClick={() => {
                                setTransferEntry({
                                  entryId: selectedSeat!.entryId,
                                  fromSeatNo: selectedSeatNo,
                                });
                                setActiveTab('transfer');
                                setSelectedSeatNo(null);
                              }}
                              className="px-3 rounded border border-base-content/30 text-base-content/70 text-xs min-h-[44px] hover:bg-base-100"
                            >
                              {t('transfer')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                {/* Clear seats / Break table */}
                {data.assignment && (
                  <div className="space-y-2">
                    {(data.tournament?.status === 'COMPLETED' ||
                      data.tournament?.status === 'CANCELLED') &&
                      data.seats.length > 0 && (
                        <button
                          onClick={() => {
                            if (confirm(t('confirmClearSeats'))) {
                              clearSeatsMutation.mutate();
                            }
                          }}
                          disabled={clearSeatsMutation.isPending}
                          className="w-full py-2.5 rounded-lg border border-info/50 text-info text-sm font-medium hover:bg-info/10 transition flex items-center justify-center gap-2"
                        >
                          <span className="material-symbols-outlined text-lg">event_seat</span>
                          {t('clearSeats')}
                        </button>
                      )}
                    {isSettling ? (
                      <button
                        onClick={() => {
                          // Collect chip inputs for all seated players
                          const seatedEntries = data.seats
                            .filter(
                              (s) =>
                                s.entry &&
                                (s.entry.status === 'PLAYING' || s.entry.status === 'FINAL_TABLE'),
                            )
                            .map((s) => ({
                              entryId: s.entryId,
                              currentChips:
                                parseInt(
                                  chipInputs[s.entryId] ?? String(s.entry!.currentChips),
                                  10,
                                ) || 0,
                            }));
                          if (seatedEntries.length === 0) return;
                          batchChipsMutation.mutate(seatedEntries);
                        }}
                        disabled={batchChipsMutation.isPending}
                        className="w-full py-2.5 rounded-lg bg-amber-600/20 border border-amber-500/50 text-amber-300 text-sm font-medium hover:bg-amber-600/30 transition flex items-center justify-center gap-2"
                      >
                        <span className="material-symbols-outlined text-lg">check_circle</span>
                        {t('chipCountDone', { defaultValue: '籌碼點算完畢' })}
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (confirm(t('confirmBreakTable'))) {
                            breakTableMutation.mutate();
                          }
                        }}
                        disabled={breakTableMutation.isPending}
                        className="w-full py-2.5 rounded-lg border border-warning/50 text-warning text-sm font-medium hover:bg-warning/10 transition flex items-center justify-center gap-2"
                      >
                        <span className="material-symbols-outlined text-lg">dangerous</span>
                        {t('breakTable')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ====== PLAYER ACTIONS TAB ====== */}
            {activeTab === 'actions' && (
              <div className="space-y-5">
                {/* Quick register */}
                <section>
                  <h3 className="text-sm font-semibold text-base-content/60 uppercase tracking-wider mb-2">
                    {t('quickRegister')}
                  </h3>

                  {/* Member search */}
                  <input
                    type="text"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder={t('searchMemberPlaceholder')}
                    className="w-full px-3 py-2 rounded-lg bg-base-100 border border-base-300 text-sm mb-2"
                  />
                  {memberSearch.length >= 1 && searchResults.length > 0 && (
                    <div className="space-y-1 max-h-[160px] overflow-y-auto mb-3">
                      {searchResults.map((r) => (
                        <button
                          key={r.member.id}
                          onClick={() => registerMutation.mutate({ memberId: r.member.id })}
                          disabled={registerMutation.isPending || r.isBlacklisted}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between min-h-[44px] ${
                            r.isBlacklisted
                              ? 'bg-red-500/10 opacity-50 cursor-not-allowed'
                              : 'bg-base-100 hover:bg-primary/10'
                          }`}
                        >
                          <div>
                            <span className="font-medium">{r.member.nickname}</span>
                            <span className="text-xs text-base-content/50 ml-2">
                              #{r.member.memberNo}
                            </span>
                          </div>
                          <div className="flex gap-1">
                            {r.hasDebt && (
                              <span className="text-xs text-red-400">{t('hasDebt')}</span>
                            )}
                            {r.isBlacklisted && (
                              <span className="text-xs text-red-400">{t('blacklisted')}</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Anonymous player */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={anonymousName}
                      onChange={(e) => setAnonymousName(e.target.value)}
                      placeholder={t('anonymousNamePlaceholder')}
                      className="flex-1 px-3 py-2 rounded-lg bg-base-100 border border-base-300 text-sm"
                    />
                    <button
                      onClick={() => {
                        if (anonymousName.trim()) {
                          registerMutation.mutate({ playerName: anonymousName.trim() });
                        }
                      }}
                      disabled={!anonymousName.trim() || registerMutation.isPending}
                      className="px-4 py-2 rounded-lg bg-primary text-primary-content text-sm font-medium min-h-[44px]"
                    >
                      {t('register', { ns: 'tournament' })}
                    </button>
                  </div>
                </section>

                {/* Pending check-in */}
                {registeredEntries.length > 0 && (
                  <section>
                    <h3 className="text-sm font-semibold text-base-content/60 uppercase tracking-wider mb-2">
                      {t('pendingCheckIn')} ({registeredEntries.length})
                    </h3>
                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                      {registeredEntries.map((entry) => (
                        <div
                          key={entry.id}
                          className="bg-base-300 rounded-lg px-3 py-2 flex items-center justify-between min-h-[44px]"
                        >
                          <span className="text-sm">{entry.playerName}</span>
                          <button
                            onClick={() => checkInMutation.mutate(entry.id)}
                            disabled={checkInMutation.isPending}
                            className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium min-h-[44px]"
                          >
                            {t('checkIn', { ns: 'tournament' })}
                          </button>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Active players */}
                {activeEntries.length > 0 && (
                  <section>
                    <h3 className="text-sm font-semibold text-base-content/60 uppercase tracking-wider mb-2">
                      {t('activePlayers')} ({activeEntries.length})
                    </h3>
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {activeEntries.map((entry) => (
                        <div key={entry.id} className="bg-base-300 rounded-lg p-3">
                          {/* Row 1: Player info */}
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              {entry.seatNo != null && (
                                <span className="text-xs text-base-content/50 font-mono">
                                  {entry.tableNo ? `T${entry.tableNo} ` : ''}S{entry.seatNo}
                                </span>
                              )}
                              <span className="font-medium text-sm">{entry.playerName}</span>
                              <span
                                className={`text-xs px-1.5 py-0.5 rounded ${
                                  entry.status === 'PLAYING'
                                    ? 'bg-emerald-500/20 text-emerald-400'
                                    : 'bg-blue-500/20 text-blue-400'
                                }`}
                              >
                                {entry.status === 'FINAL_TABLE' ? 'FT' : 'P'}
                              </span>
                            </div>
                            {entry.currentChips > 0 && (
                              <span className="text-xs text-primary font-mono">
                                {entry.currentChips.toLocaleString()}
                              </span>
                            )}
                          </div>

                          {/* Row 2: Stats (total chips, R/A counts) */}
                          <div className="flex items-center gap-3 mb-2 text-[10px] text-base-content/40">
                            <span>
                              {t('totalBuyIn', { defaultValue: '總買入' })}:{' '}
                              {entry.totalBuyInChips?.toLocaleString()}
                            </span>
                            {entry.rebuyCount > 0 && (
                              <span className="text-blue-400">R ×{entry.rebuyCount}</span>
                            )}
                            {entry.addonCount > 0 && (
                              <span className="text-purple-400">A ×{entry.addonCount}</span>
                            )}
                          </div>

                          <div className="flex gap-2 flex-wrap">
                            {/* Eliminate */}
                            {confirmEliminate === entry.id ? (
                              <button
                                onClick={() =>
                                  eliminateMutation.mutate({ entryId: entry.id, rank: aliveCount })
                                }
                                disabled={eliminateMutation.isPending}
                                className="px-3 rounded bg-red-600 text-white text-xs font-medium min-h-[44px]"
                              >
                                {t('confirmEliminateRank', { rank: aliveCount })}
                              </button>
                            ) : (
                              <button
                                onClick={() => setConfirmEliminate(entry.id)}
                                className="px-3 rounded border border-red-500/50 text-red-400 text-xs font-medium min-h-[44px] hover:bg-red-500/10"
                              >
                                {t('eliminate', { ns: 'tournament' })}
                              </button>
                            )}
                            {/* Rebuy */}
                            {canRebuy && (
                              <button
                                onClick={() =>
                                  rebuyAddonMutation.mutate({ entryId: entry.id, type: 'REBUY' })
                                }
                                disabled={rebuyAddonMutation.isPending}
                                className="px-3 rounded border border-blue-500/50 text-blue-400 text-xs font-medium min-h-[44px] hover:bg-blue-500/10"
                              >
                                Rebuy +{tournamentDetail?.rebuyChips.toLocaleString()}
                              </button>
                            )}
                            {/* Addon */}
                            {canAddon && (
                              <button
                                onClick={() =>
                                  rebuyAddonMutation.mutate({ entryId: entry.id, type: 'ADDON' })
                                }
                                disabled={rebuyAddonMutation.isPending}
                                className="px-3 rounded border border-purple-500/50 text-purple-400 text-xs font-medium min-h-[44px] hover:bg-purple-500/10"
                              >
                                Addon +{tournamentDetail?.addonChips.toLocaleString()}
                              </button>
                            )}
                            {/* Correction */}
                            {(entry.rebuyCount > 0 || entry.addonCount > 0) && (
                              <button
                                onClick={() => {
                                  setCorrectingEntry(entry);
                                  setCorrectPassword('');
                                  setCorrectError('');
                                }}
                                className="px-3 rounded border border-amber-500/50 text-amber-400 text-xs font-medium min-h-[44px] hover:bg-amber-500/10"
                              >
                                {t('correct', { defaultValue: '修正' })}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}

            {/* ====== TRANSFER TAB ====== */}
            {activeTab === 'transfer' && (
              <div className="space-y-4">
                {/* Step 1: Select source player */}
                <section>
                  <h3 className="text-sm font-semibold text-base-content/60 uppercase tracking-wider mb-2">
                    {t('selectPlayer')}
                  </h3>
                  {seatedPlayers.length === 0 ? (
                    <p className="text-sm text-base-content/50">{t('noSeatedPlayers')}</p>
                  ) : (
                    <div className="space-y-1">
                      {seatedPlayers.map((p) => (
                        <button
                          key={p.entryId}
                          onClick={() => {
                            setTransferEntry(
                              transferEntry?.entryId === p.entryId
                                ? null
                                : { entryId: p.entryId, fromSeatNo: p.seatNo },
                            );
                            setTargetTableId('');
                            setTargetSeatNo(null);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between min-h-[44px] ${
                            transferEntry?.entryId === p.entryId
                              ? 'bg-primary/20 border border-primary/40'
                              : 'bg-base-300 hover:bg-base-100'
                          }`}
                        >
                          <span>
                            S{p.seatNo} — {p.playerName}
                          </span>
                          {transferEntry?.entryId === p.entryId && (
                            <span className="text-xs text-primary">{t('selected')}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </section>

                {/* Step 2: Select target table */}
                {transferEntry && (
                  <section>
                    <h3 className="text-sm font-semibold text-base-content/60 uppercase tracking-wider mb-2">
                      {t('targetTable')}
                    </h3>
                    {otherTables.length === 0 ? (
                      <p className="text-sm text-base-content/50">{t('noOtherTables')}</p>
                    ) : (
                      <select
                        value={targetTableId}
                        onChange={(e) => {
                          setTargetTableId(e.target.value);
                          setTargetSeatNo(null);
                        }}
                        className="w-full px-3 py-2 rounded-lg bg-base-100 border border-base-300 text-sm min-h-[44px]"
                      >
                        <option value="">{t('selectTargetTable')}</option>
                        {otherTables.map((ot) => {
                          const empty = ot.table.maxSeats - ot.seats.length;
                          return (
                            <option key={ot.table.id} value={ot.table.id}>
                              {t('tableWithEmptySeats', { no: ot.table.tableNo, empty })}
                            </option>
                          );
                        })}
                      </select>
                    )}
                  </section>
                )}

                {/* Step 3: Select target seat */}
                {transferEntry && targetTable && (
                  <section>
                    <h3 className="text-sm font-semibold text-base-content/60 uppercase tracking-wider mb-2">
                      {t('selectSeatForTable', { no: targetTable.table.tableNo })}
                    </h3>
                    <div className="grid grid-cols-5 gap-2">
                      {Array.from({ length: targetTable.table.maxSeats }, (_, i) => i + 1).map(
                        (seatNo) => {
                          const occupied = targetTable.seats.some((s) => s.seatNo === seatNo);
                          const isSelected = targetSeatNo === seatNo;
                          return (
                            <button
                              key={seatNo}
                              onClick={() =>
                                !occupied && setTargetSeatNo(isSelected ? null : seatNo)
                              }
                              disabled={occupied}
                              className={`rounded-lg text-center text-sm font-medium min-h-[44px] transition ${
                                occupied
                                  ? 'bg-base-300 text-base-content/30 cursor-not-allowed'
                                  : isSelected
                                    ? 'bg-primary text-primary-content'
                                    : 'bg-base-100 border border-base-300 hover:border-primary/50'
                              }`}
                            >
                              {seatNo}
                            </button>
                          );
                        },
                      )}
                    </div>
                  </section>
                )}

                {/* Step 4: Confirm transfer */}
                {transferEntry && targetTableId && targetSeatNo && (
                  <button
                    onClick={() =>
                      transferMutation.mutate({
                        entryId: transferEntry.entryId,
                        fromTableId: data.table.id,
                        fromSeatNo: transferEntry.fromSeatNo,
                        toTableId: targetTableId,
                        toSeatNo: targetSeatNo,
                      })
                    }
                    disabled={transferMutation.isPending}
                    className="w-full py-3 rounded-lg bg-primary text-primary-content text-sm font-medium min-h-[48px]"
                  >
                    {transferMutation.isPending
                      ? t('transferring')
                      : t('confirmTransfer', {
                          no: targetTable?.table.tableNo,
                          seat: targetSeatNo,
                        })}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ====== CORRECTION MODAL ====== */}
      {correctingEntry && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-base-200 rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold">
                {t('correctTitle', { defaultValue: '修正 Rebuy/Addon' })} —{' '}
                {correctingEntry.playerName}
              </h3>
              <button
                onClick={() => setCorrectingEntry(null)}
                className="text-base-content/40 hover:text-base-content text-lg"
              >
                &times;
              </button>
            </div>

            {/* Player summary */}
            <div className="text-sm text-base-content/60 space-y-1">
              <div>
                {t('currentChips', { defaultValue: '目前籌碼' })}:{' '}
                <span className="text-primary font-mono">
                  {correctingEntry.currentChips.toLocaleString()}
                </span>
              </div>
              <div>
                {t('totalBuyIn', { defaultValue: '總買入' })}:{' '}
                <span className="font-mono">
                  {correctingEntry.totalBuyInChips?.toLocaleString()}
                </span>
              </div>
              <div>
                Rebuy ×{correctingEntry.rebuyCount}　Addon ×{correctingEntry.addonCount}
              </div>
            </div>

            {/* Rebuy/Addon history */}
            <div>
              <h4 className="text-xs font-semibold text-base-content/50 mb-2">
                {t('raHistory', { defaultValue: '操作歷史（點擊撤銷）' })}
              </h4>
              {correctingLogs.length === 0 ? (
                <p className="text-xs text-base-content/40">
                  {t('noLogs', { defaultValue: '無可撤銷的紀錄' })}
                </p>
              ) : (
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {correctingLogs.map((log) => (
                    <button
                      key={log.id}
                      onClick={() => {
                        if (!correctPassword) {
                          setCorrectError('請先輸入密碼');
                          return;
                        }
                        correctMutation.mutate({ logId: log.id, password: correctPassword });
                      }}
                      disabled={correctMutation.isPending || log.type === 'REENTRY'}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-base-100 hover:bg-red-500/10 text-sm min-h-[44px] border border-base-300 hover:border-red-500/30 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                            log.type === 'REBUY'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-purple-500/20 text-purple-400'
                          }`}
                        >
                          {log.type}
                        </span>
                        <span>+{log.chipsAdded.toLocaleString()}</span>
                        <span className="text-base-content/40">Lv.{log.atLevel}</span>
                      </div>
                      <span className="text-red-400 text-xs">
                        {t('undoAction', { defaultValue: '撤銷' })}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Password verification */}
            <div>
              <label className="block text-xs text-base-content/50 mb-1">
                {t('passwordVerify', { defaultValue: '密碼驗證（雙重認證）' })}
              </label>
              <input
                type="password"
                value={correctPassword}
                onChange={(e) => {
                  setCorrectPassword(e.target.value);
                  setCorrectError('');
                }}
                placeholder={t('enterPassword', { defaultValue: '輸入您的登入密碼' })}
                className="w-full px-3 py-2 rounded-lg bg-base-100 border border-base-300 text-sm min-h-[44px]"
              />
            </div>

            {correctError && <p className="text-xs text-red-400">{correctError}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
